import React, { useCallback, useEffect, useMemo, useState } from "react";
import AdditionalHomeSiteDetails from "./CoreHomeSiteDetails/AdditionalHomeSiteDetails/AdditionalHomeSiteDetails";
import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import colors from "../../../../components/colors/colors.module.scss";
import BasicActionsDetails from "./CoreHomeSiteDetails/BasicActionDetails/BasicActionsDetails";
import { useSelector } from "react-redux";
import { getAuth } from "../../../../redux/auth/authSlice";
import {
  useHomeSiteDataById,
  useUpdateHomeSite,
} from "../../../../reactQuery/homeSites/homsites/homeSiteQuery";
import { useMileStonTaksDataByHomesiteId } from "../../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";
import { useNotesDataByHomesiteId } from "../../../../reactQuery/homeSites/notes/homeSiteNotes";
import { useExceptionsDataByHomesiteId } from "../../../../reactQuery/homeSites/exceptions/homeSiteExceptions";
import { useAlertsDataByHomesiteId } from "../../../../reactQuery/homeSites/alerts/homesiteAlerts";
import { IHomesite } from "../../../../api/homesites/homesites.types";
import { useUpdateMilestoneTasksById } from "../../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";
import { useDialogBoxContext } from "../../../../components/decisionDialogBox/decisionDialogBoxContext";
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from "../../../../components/decisionDialogBox/decisionDialogBox";
import { useDivisionMileStoneDataReleased } from "../../../../reactQuery/division/divisionMileStoneQuery";
import { DivisionMileStone } from "../../../../api/divisionMileStones/types";

interface CoreHomeSiteDetailsProps {
  userId: string;
  homeSiteDetails: IHomesite;
  tab?: string;
  milestoneFilter?: string;
  expandMilestone?: string;
  onUnsavedChangesChange: (hasUnsaved: boolean) => void;
  onChildHandlersReady?: (handlers: {
    save: () => Promise<void>;
    cancel: () => void;
  }) => void;
}

const tabMap: Record<string, number> = {
  basic: 0,
  additional: 1,
};

const CoreHomeSiteDetails: React.FC<CoreHomeSiteDetailsProps> = ({ homeSiteDetails: initialHomesiteData, userId, tab, milestoneFilter, expandMilestone, onUnsavedChangesChange, onChildHandlersReady }) => {
  const { userInfo } = useSelector(getAuth);
  const homeSiteDetailsId = initialHomesiteData.homeSiteId;
  const [homesiteData, setHomesiteData] = useState<IHomesite>(initialHomesiteData);
  const { data: divisionsData } = useDivisionMileStoneDataReleased(userInfo.userDivCode);
  const { data: milestoneTasksData, isLoading: isLoadingMilestoneTasks } = useMileStonTaksDataByHomesiteId(userInfo.userDivCode, homeSiteDetailsId);
  const { data: updateHomesiteData, isLoading: isLoadingHomesite, isSuccess: combinedSuccess } = useHomeSiteDataById(
    userInfo.userDivCode,
    homeSiteDetailsId,
    initialHomesiteData.communityCode,
    initialHomesiteData.communityPhase
  );
  const { mutate: updateMilestoneTasks } = useUpdateMilestoneTasksById(userInfo.userDivCode, homeSiteDetailsId);
  const { mutate: updateHomesite } = useUpdateHomeSite(userInfo.userDivCode);
  const { data: notes, isLoading: isLoadingNotes } = useNotesDataByHomesiteId(userInfo.userDivCode, homeSiteDetailsId);
  const { data: exceptions, isLoading: isLoadingExceptions } = useExceptionsDataByHomesiteId(userInfo.userDivCode, homeSiteDetailsId);
  const { data: alerts, isLoading: isLoadingAlerts } = useAlertsDataByHomesiteId(userInfo.userDivCode, homeSiteDetailsId);
  const [selectedTab, setSelectedTab] = useState(0);
  const { showDecisionDialogBox } = useDialogBoxContext();
  const [unsavedChangesInAdditional, setUnsavedChangesInAdditional] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notesDirty, setNotesDirty] = useState(false);
  const [childSave, setChildSave] = useState<() => Promise<void>>(
    () => async () => { }
  );
  const [childCancel, setChildCancel] = useState<() => void>(() => { });
  const [milestoneTab, setMilestoneTab] = useState<string>(tab || "");

  /* Update state and notify parent component of child handlers readiness */
  const handleChildHandlersReady = useCallback(
    (handlers: { save: () => Promise<void>; cancel: () => void }) => {
      setChildSave(() => handlers.save);
      setChildCancel(() => handlers.cancel);
      onChildHandlersReady?.(handlers);
    },
    [onChildHandlersReady]
  );

  /* Update state and notify parent component of unsaved changes status */
  const handleUnsavedChanges = useCallback(
    (hasUnsaved: boolean) => {
      setUnsavedChangesInAdditional(hasUnsaved);
      onUnsavedChangesChange?.(hasUnsaved);
    },
    [onUnsavedChangesChange]
  );

  // Group by milestone and their tasks
  const orderedMilestones = useMemo(() => {
    if (!divisionsData?.data) return [];
    // Group milestones by mileStoneName, and collect their tasks
    const milestoneMap: Record<string, DivisionMileStone & { tasks: DivisionMileStone[] }> = {};
    divisionsData.data.forEach((item) => {
      if (!item || !item.mileStoneName) return;
      if (!milestoneMap[item.mileStoneName]) {
        milestoneMap[item.mileStoneName] = {
          ...item,
          tasks: [],
        };
      }
      milestoneMap[item.mileStoneName].tasks.push(item);
    });
    // Return as an array, sorted by milestoneOrder if available
    return Object.values(milestoneMap).sort(
      (a, b) => (a.mileStoneOrder ?? 0) - (b.mileStoneOrder ?? 0)
    );
  }, [divisionsData]);

  const divisionTasks = divisionsData?.data
    .flatMap((division) => division)
    .slice()
    .sort((a, b) => (a.taskOrder ?? 0) - (b.taskOrder ?? 0)) || [];


  const taskIndexMap = divisionTasks.reduce((acc, task, index) => {
    acc[task.taskName.toLowerCase()] = index;
    return acc;
  }, {} as Record<string, number>);

  const sortedMilestoneTasks =
    milestoneTasksData?.data?.sort((a, b) => {
      const indexA =
        taskIndexMap[a.taskName.toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
      const indexB =
        taskIndexMap[b.taskName.toLowerCase()] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    }) || [];

  useEffect(() => {
    if (tab) {
      const mappedTab = tabMap[tab.toLowerCase()] ?? 0;
      setSelectedTab(mappedTab);
    }
  }, [tab]);

  useEffect(() => {
    if (updateHomesiteData && combinedSuccess) {
      setHomesiteData({
        ...homesiteData,
        ...updateHomesiteData
      });
    }
  }, [updateHomesiteData, combinedSuccess, homesiteData]);

  useEffect(() => {
    if (tab && tab !== milestoneTab) {
      setMilestoneTab(tab);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    event.stopPropagation();
    if (
      (selectedTab === 0 && unsavedChangesInAdditional) ||
      (selectedTab === 0 && notesDirty) ||
      (selectedTab === 1 && unsavedChangesInAdditional)
    ) {
      showDecisionDialogBox(
        true,
        decisionDialogBoxTitle,
        decisionDialogBoxMessage,
        async () => {
          await childSave();
          setSelectedTab(newValue);
        },
        () => {
          childCancel();
          setSelectedTab(newValue);
        }
      );
    } else {
      setSelectedTab(newValue);
    }
  };

  const isLoading =
    isLoadingMilestoneTasks ||
    isLoadingHomesite ||
    isLoadingNotes ||
    isLoadingExceptions ||
    isLoadingAlerts;
  const renderTabContent = () => {
    switch (selectedTab) {
      case 0:
        return (
          <BasicActionsDetails
            userId={userId}
            homeSiteId={homeSiteDetailsId}
            milestoneTasks={sortedMilestoneTasks || []}
            milestoneState={orderedMilestones || []}
            notesState={notes?.data || []}
            exceptionsState={exceptions?.data || []}
            alertState={alerts?.data || []}
            tab={tab}
            updateMilestoneTasks={updateMilestoneTasks}
            divisionCode={homesiteData?.divisionCode || ""}
            conciergeEmail={homesiteData?.conciergeEmail || ""}
            opportunityId={homesiteData?.opportunityId || ""}
            saleDate={homesiteData?.saleDate}
            closeOfEscrowActual={homesiteData?.closeOfEscrowActual}
            closeOfEscrowEstimated={homesiteData?.closeOfEscrowEstimated}
            milestoneFilter={milestoneFilter}
            expandMilestone={expandMilestone}
            setChildHandlers={handleChildHandlersReady}
            isDirty={handleUnsavedChanges}
            setMilestoneTab={setMilestoneTab}
            milestoneTab={milestoneTab}
          />
        );
      case 1:
        return (
          <AdditionalHomeSiteDetails
            homesite={homesiteData}
            updateHomesite={updateHomesite}
            divisionCode={homesiteData?.divisionCode || ""}
            conciergeEmail={homesiteData?.conciergeEmail || ""}
            onUnsavedChangesChange={handleUnsavedChanges}
            setChildHandlers={handleChildHandlersReady}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "1020px", height: "100%", minHeight: "48px" }}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Core Home Site Details Tabs"
        sx={tabsStyles}
      >
        <Tab label="Basic Details" />
        <Tab label="Additional Details" />
      </Tabs>
      <Box sx={{ padding: "16px 2px" }}>{renderTabContent()}</Box>
    </Box>
  );
};

// Extracted styles for better reusability and readability
const tabsStyles = {
  borderBottom: 1,
  borderColor: "divider",
  "& .MuiTabs-flexContainer": {
    justifyContent: "center",
  },
  "& .MuiTab-root": {
    fontSize: "16px",
    textTransform: "none",
    minWidth: "unset",
    padding: "12px 0",
    margin: "0 15%",
  },
  "& .MuiTab-root.Mui-selected": {
    color: colors.blue5,
  },
  "& .MuiTabs-indicator": {
    backgroundColor: colors.blue5,
    height: 2,
    minWidth: "600px",
    transform: "translateX(-40%)",
    left: "40%",
  },
};

export default CoreHomeSiteDetails;
